/**
 * Small schedule/session-derived decisions the UI makes, pulled out of the
 * components so they can be unit-tested without a DOM.
 */

import type { ScheduleConfig, TenantHealth } from '@irrigation/core';
import { sessionPhase } from '@irrigation/core';
import { toIsoDate } from './format.js';

/**
 * True when the watering window is open right now but no plan exists for the
 * session it belongs to — a genuine gap (e.g. a planner outage), since the
 * scheduler plans each session before its window opens. `now` is epoch ms; the
 * session date is derived in browser-local time to match the rest of the page.
 */
export function noPlanForOpenWindow(
  config: ScheduleConfig,
  health: TenantHealth | null,
  now: number,
): boolean {
  const nowLocal = new Date(now);
  const { sessionDate, open } = sessionPhase(
    toIsoDate(nowLocal),
    nowLocal.getHours() * 60 + nowLocal.getMinutes(),
    config.window,
  );
  return open && health?.lastPlannedDate !== sessionDate;
}

/**
 * Validate the watering-window endpoints. A window that wraps past midnight
 * (`latestEnd` earlier than `earliestStart`, e.g. 22:00 → 06:00) is allowed;
 * only identical endpoints are meaningless. Returns an error message, or
 * undefined when valid.
 */
export function windowError(earliestStart: string, latestEnd: string): string | undefined {
  if (earliestStart === latestEnd) return 'Must differ from the earliest start.';
  return undefined;
}
